import type { NextFn } from '@warp-drive/core/request';
import type { RequestContext } from '@warp-drive/core/types/request';

const DEFAULT_TOAST_PREFIX: Record<number, string> = {
  400: 'Bad request: ',
  403: 'Access denied: ',
};

const FALLBACK_ERROR_MESSAGE =
  'An error occurred. Please try again or contact support if the problem persists.';

export interface ResponseHandlerOptions {
  /** True while an authenticated session exists; the handler no-ops otherwise. */
  isAuthenticated: () => boolean;
  /** Invoked when {@link shouldInvalidate} returns true (e.g. logout/invalidate). */
  onUnauthorized: () => void;
  /**
   * Predicate selecting statuses that trigger {@link onUnauthorized}. Defaults
   * to `status === 401`. tio-agent overrides this to also invalidate on 403.
   */
  shouldInvalidate?: (status: number) => boolean;
  /** Statuses that surface a user-visible toast (employee/admin: 400/403/422). */
  toastStatuses?: number[];
  /** Renders a notification. Required if `toastStatuses` is non-empty. */
  showToast?: (message: string) => void;
  /** Side-channel error reporter (e.g. Datadog `addError`). */
  reportError?: (message: string, ctx: Record<string, unknown>) => void;
  /** Optional user id getter, attached to reportError context for triage. */
  getUserId?: () => string | undefined;
}

interface RequestError extends Error {
  status?: number;
  statusText?: string;
  isRequestError?: boolean;
  content?: unknown;
}

function defaultShouldInvalidate(status: number) {
  return status === 401;
}

function extractErrorMessage(payload: unknown): string {
  if (!payload || typeof payload !== 'object') return FALLBACK_ERROR_MESSAGE;
  const p = payload as Record<string, unknown>;

  if (Array.isArray(p.errors) && p.errors.length > 0) {
    const first = p.errors[0] as Record<string, unknown>;
    return (first.detail as string) || (first.title as string) || FALLBACK_ERROR_MESSAGE;
  }
  if (typeof p.error === 'string') return p.error;
  if (typeof p.message === 'string') return p.message;
  return FALLBACK_ERROR_MESSAGE;
}

/**
 * Catches errors thrown by downstream handlers (typically Fetch on non-2xx)
 * and mirrors `ApplicationAdapter#ensureResponseAuthorized`: invalidate the
 * session on 401, surface a toast on configured 4xx statuses, and forward
 * to Datadog. The original error is always re-thrown so callers can still
 * `try/catch` it.
 *
 * Skipped entirely when {@link ResponseHandlerOptions.isAuthenticated}
 * returns false — pre-login flows (forgot-password, enroll) own their own
 * error handling and shouldn't be force-logged-out by a stale 401.
 */
export class TioResponseHandler {
  private shouldInvalidate: (status: number) => boolean;
  private toastStatuses: Set<number>;

  constructor(private options: ResponseHandlerOptions) {
    this.shouldInvalidate = options.shouldInvalidate ?? defaultShouldInvalidate;
    this.toastStatuses = new Set(options.toastStatuses ?? []);
  }

  async request<T>(context: RequestContext, next: NextFn<T>) {
    try {
      return await next(context.request);
    } catch (raw) {
      const error = raw as RequestError;
      const status = error?.status;

      if (typeof status === 'number' && error.isRequestError && this.options.isAuthenticated()) {
        this.handleStatus(status, error, context);
      }

      throw raw;
    }
  }

  private handleStatus(status: number, error: RequestError, context: RequestContext) {
    const userId = this.options.getUserId?.();
    const baseCtx = {
      status,
      payload: error.content,
      url: context.request.url,
      method: context.request.method ?? 'GET',
      userId,
    };

    if (this.shouldInvalidate(status)) {
      console.error('TioResponseHandler caught unauthorized response', baseCtx);
      this.options.reportError?.(
        `TioResponseHandler caught error status ${status} for user ${userId ?? 'unknown'}`,
        baseCtx
      );
      this.options.onUnauthorized();
      return;
    }

    if (this.toastStatuses.has(status)) {
      const raw = extractErrorMessage(error.content);
      const prefixed = `${DEFAULT_TOAST_PREFIX[status] ?? ''}${raw}`;

      console.warn('TioResponseHandler caught non-fatal error', baseCtx);
      try {
        this.options.showToast?.(prefixed);
      } catch (toastErr) {
        console.error('Failed to show error notification:', toastErr, 'Original:', prefixed);
      }
      this.options.reportError?.(
        `TioResponseHandler caught non-fatal error status ${status} for user ${userId ?? 'unknown'}`,
        { ...baseCtx, errorMessage: prefixed }
      );
    }
  }
}
